import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import Logo from "./Logo";
import LanguageSwitcher from "./LanguageSwitcher";
import {
  Menu, X, ChevronDown, ChevronRight, LogOut, LayoutDashboard, ShieldCheck,
  Phone, Mail, User, ArrowRight, Code2, Smartphone, Search, Server, Wrench,
  Building2, BookOpen, FileText, MessageSquare, Sparkles, HeadphonesIcon,
  ImageIcon,
} from "lucide-react";

const MENU_GROUPS = [
  {
    key: "it-services",
    label: "IT Services",
    items: [
      { to: "/services/web-development", label: "Web Development", desc: "Custom web apps & SaaS", icon: Code2 },
      { to: "/services/android-development", label: "Android Apps", desc: "Native & cross-platform", icon: Smartphone },
      { to: "/services/seo", label: "SEO Services", desc: "Rank higher · convert better", icon: Search },
    ],
  },
  {
    key: "infrastructure",
    label: "Infrastructure",
    items: [
      { to: "/services/server-management", label: "Server Management", desc: "Physical & virtual · 24/7 NOC", icon: Server },
      { to: "/services/hardware-repair", label: "Hardware & Software Repair", desc: "Laptops · Printers · Diagnostics", icon: Wrench },
    ],
  },
  {
    key: "company",
    label: "Company",
    items: [
      { to: "/about", label: "About Us", desc: "Engineers behind UID Infotech", icon: Building2 },
      { to: "/pricing", label: "Pricing", desc: "UID Care ₹999/mo · Pay-as-you-go", icon: Sparkles, badge: "NEW" },
      { to: "/contact", label: "Contact", desc: "Talk to a real person", icon: MessageSquare },
    ],
  },
  {
    key: "resources",
    label: "Resources",
    items: [
      { to: "/tools", label: "Free Image Tools", desc: "Compress · Resize · Passport / Signature · PDF", icon: ImageIcon, badge: "NEW" },
      { to: "/request", label: "Request a Quote", desc: "Tell us about your project", icon: FileText },
      { to: "/#leaderboard", label: "Trust Signals", desc: "Live CSAT & top engineers", icon: Sparkles },
      { to: "/contact", label: "24/7 Helpline", desc: "We pick up — promise", icon: HeadphonesIcon },
    ],
  },
];

function DesktopDropdown({ group }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <button
        type="button"
        className="inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium text-white/80 hover:text-white hover:bg-white/[0.06] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/50"
        data-testid={`nav-${group.key}-toggle`}
        aria-expanded={open}
        aria-haspopup="true"
      >
        {t(`header.nav.groups.${group.key}`, { defaultValue: group.label })}
        <ChevronDown className={`h-3.5 w-3.5 text-white/50 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <div
        className={`absolute left-1/2 top-full -translate-x-1/2 pt-3 w-[320px] transition-all duration-200 z-50 ${
          open ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-1 pointer-events-none"
        }`}
        data-testid={`nav-${group.key}-menu`}
      >
        <div className="relative rounded-2xl border border-white/10 bg-[#111827]/95 backdrop-blur-xl p-2 shadow-2xl shadow-black/40">
          <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 h-3 w-3 rotate-45 border-l border-t border-white/10 bg-[#111827]" />
          {group.items.map((it) => {
            const Icon = it.icon;
            return (
              <Link
                key={it.to + it.label}
                to={it.to}
                className="flex items-start gap-3 rounded-xl px-3 py-2.5 hover:bg-white/[0.04] transition-colors group"
                data-testid={it.to.startsWith("/services/") ? `nav-service-${it.to.split("/").pop()}` : `nav-item-${it.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-blue-500/20 to-cyan-400/15 border border-white/10 group-hover:border-blue-400/40 transition-colors">
                  <Icon className="h-4 w-4 text-blue-300" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white flex items-center gap-1.5">
                    {it.label}
                    {it.badge && (
                      <span className="inline-flex items-center rounded-full bg-amber-500/15 border border-amber-500/40 px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wider text-amber-300">
                        {it.badge}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-white/50 mt-0.5">{it.desc}</div>
                </div>
                <ChevronRight className="ml-auto h-4 w-4 text-white/30 group-hover:text-blue-300 group-hover:translate-x-0.5 transition-all" />
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function Header() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileGroup, setMobileGroup] = useState(null);

  // Close mobile menu on route navigation
  useEffect(() => {
    if (!mobileOpen) return;
    const close = () => setMobileOpen(false);
    window.addEventListener("popstate", close);
    return () => window.removeEventListener("popstate", close);
  }, [mobileOpen]);

  const dashboardPath =
    user?.role === "admin" ? "/admin" : user?.role === "staff" ? "/staff" : "/dashboard";

  return (
    <header className="sticky top-0 z-40 w-full">
      {/* ── Top Utility Bar ── */}
      <div className="bg-[#060A14] border-b border-white/[0.06]" data-testid="utility-bar">
        <div className="container-x flex h-9 items-center justify-between text-[12px] text-white/55">
          <div className="flex items-center gap-5 sm:gap-7">
            <a href="tel:+918055592955" className="inline-flex items-center gap-1.5 hover:text-white transition-colors" data-testid="utility-phone">
              <Phone className="h-3.5 w-3.5 text-blue-400" />
              <span className="hidden sm:inline">24/7 Helpline:</span>
              <span className="font-medium">+91 80555 92955</span>
            </a>
            <a href="mailto:support@uidinfotech.online" className="hidden sm:inline-flex items-center gap-1.5 hover:text-white transition-colors" data-testid="utility-email">
              <Mail className="h-3.5 w-3.5 text-blue-400" />
              support@uidinfotech.online
            </a>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden md:inline-flex items-center gap-1.5 text-emerald-300/80">
              <span className="relative grid h-2 w-2 place-items-center">
                <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />
                <span className="relative h-1.5 w-1.5 rounded-full bg-emerald-400" />
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-wider">NOC online</span>
            </span>
            {user ? (
              <div className="flex items-center gap-3">
                <Link
                  to={dashboardPath}
                  className="inline-flex items-center gap-1.5 hover:text-white transition-colors font-medium"
                  data-testid="utility-dashboard-link"
                >
                  <User className="h-3.5 w-3.5" />
                  <span>Dashboard</span>
                  <span className="hidden md:inline text-white/40">· {user.name}</span>
                </Link>
                <span className="text-white/30 hidden sm:inline">·</span>
                <LanguageSwitcher variant="compact" className="hidden sm:inline-block" />
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1.5 hover:text-white transition-colors font-medium"
                  data-testid="utility-login-link"
                >
                  <User className="h-3.5 w-3.5" />
                  {t("header.utility.client_login")}
                </Link>
                <span className="text-white/30">·</span>
                <Link
                  to="/login?role=admin"
                  className="inline-flex items-center gap-1.5 text-amber-300/90 hover:text-amber-200 transition-colors font-medium"
                  data-testid="utility-admin-login-link"
                  title="Admin / Founder access"
                >
                  <ShieldCheck className="h-3.5 w-3.5" />
                  {t("header.utility.admin_login")}
                </Link>
                <span className="text-white/30 hidden sm:inline">·</span>
                <LanguageSwitcher variant="compact" className="hidden sm:inline-block" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Main Navbar ── */}
      <div className="border-b border-white/10 bg-[#0A101D]/85 backdrop-blur-xl">
        <div className="container-x flex h-20 items-center justify-between gap-4">
          {/* Logo (left) */}
          <Link to="/" className="shrink-0" data-testid="brand-home-link">
            <Logo size={36} wordmarkClassName="text-base sm:text-lg" animatePulse />
          </Link>

          {/* Glassmorphism pill nav (center) */}
          <nav
            className="hidden lg:flex items-center gap-1 rounded-full bg-white/[0.06] backdrop-blur-md border border-white/10 px-2 py-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]"
            data-testid="nav-pill"
          >
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  isActive ? "bg-white/10 text-white" : "text-white/80 hover:text-white hover:bg-white/[0.06]"
                }`
              }
              data-testid="nav-home"
            >
              Home
            </NavLink>
            {MENU_GROUPS.map((g) => (
              <DesktopDropdown key={g.key} group={g} />
            ))}
          </nav>

          {/* Right side: Orange CTA + user actions */}
          <div className="flex items-center gap-2">
            {/* Orange "Consult Our Experts" CTA — desktop only */}
            <Link
              to="/request"
              className="hidden lg:inline-flex relative items-center gap-2 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-500 hover:to-orange-500 active:scale-[0.98] text-white font-semibold pl-6 pr-5 py-2.5 text-sm transition-all overflow-hidden shadow-[0_8px_24px_-8px_rgba(249,115,22,0.55)]"
              data-testid="header-consult-cta"
            >
              {/* Cyan glow accent on the left edge */}
              <span className="pointer-events-none absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b from-cyan-300 via-cyan-400 to-blue-500 shadow-[0_0_18px_4px_rgba(34,211,238,0.55)]" aria-hidden="true" />
              <span className="pointer-events-none absolute inset-y-1 left-1.5 w-px bg-cyan-200/40" aria-hidden="true" />
              {t("header.nav.consult_cta")}
              <ArrowRight className="h-4 w-4" />
            </Link>

            {/* Logout (only when logged-in, desktop) */}
            {user && (
              <button
                onClick={async () => { await logout(); navigate("/"); }}
                className="hidden lg:inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-medium text-white/55 hover:text-white hover:bg-white/5 transition-colors"
                data-testid="header-logout-btn"
                aria-label="Sign out"
              >
                <LogOut className="h-3.5 w-3.5" />
              </button>
            )}

            {/* Mobile-only compact language switcher (always visible) */}
            <LanguageSwitcher variant="compact" className="lg:hidden" />

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden grid h-10 w-10 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-white hover:bg-white/10 transition-colors"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* ── Mobile drawer ── */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-300 border-b border-white/10 bg-[#0A101D] ${
          mobileOpen ? "max-h-[85vh] opacity-100" : "max-h-0 opacity-0"
        }`}
        data-testid="mobile-menu"
        aria-hidden={!mobileOpen}
      >
        <div className="container-x py-4 space-y-1">
          <Link
            to="/"
            onClick={() => setMobileOpen(false)}
            className="block rounded-lg px-3 py-2.5 text-sm font-medium text-white/85 hover:bg-white/5"
            data-testid="mnav-home"
          >
            {t("header.nav.home")}
          </Link>
          {MENU_GROUPS.map((g) => {
            const expanded = mobileGroup === g.key;
            return (
              <div key={g.key} className="rounded-lg">
                <button
                  type="button"
                  onClick={() => setMobileGroup((cur) => (cur === g.key ? null : g.key))}
                  className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-sm font-medium text-white/85 hover:bg-white/5"
                  data-testid={`mnav-${g.key}-toggle`}
                  aria-expanded={expanded}
                >
                  {t(`header.nav.groups.${g.key}`, { defaultValue: g.label })}
                  <ChevronDown className={`h-4 w-4 text-white/50 transition-transform ${expanded ? "rotate-180" : ""}`} />
                </button>
                <div
                  className={`grid transition-[grid-template-rows] duration-200 ${
                    expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="pl-3 py-1 space-y-0.5">
                      {g.items.map((it) => {
                        const Icon = it.icon;
                        return (
                          <Link
                            key={it.to + it.label}
                            to={it.to}
                            onClick={() => setMobileOpen(false)}
                            className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-white/70 hover:text-white hover:bg-white/[0.04]"
                            data-testid={it.to.startsWith("/services/") ? `mnav-service-${it.to.split("/").pop()}` : `mnav-item-${it.label.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <Icon className="h-4 w-4 text-blue-300/80" />
                            {it.label}
                            {it.badge && (
                              <span className="ml-auto rounded-full bg-amber-500/15 border border-amber-500/40 px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wider text-amber-300">
                                {it.badge}
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Mobile CTA + auth */}
          <div className="pt-3 grid gap-2">
            <Link
              to="/request"
              onClick={() => setMobileOpen(false)}
              className="relative inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 text-white font-semibold px-5 py-3 text-sm overflow-hidden shadow-[0_8px_24px_-8px_rgba(249,115,22,0.55)]"
              data-testid="mnav-consult-cta"
            >
              <span className="pointer-events-none absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b from-cyan-300 via-cyan-400 to-blue-500 shadow-[0_0_18px_4px_rgba(34,211,238,0.55)]" aria-hidden="true" />
              {t("header.nav.consult_cta")} <ArrowRight className="h-4 w-4" />
            </Link>
            {!user ? (
              <div className="grid grid-cols-2 gap-2">
                <Link
                  to="/login"
                  onClick={() => setMobileOpen(false)}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/[0.04] text-white font-semibold px-4 py-2.5 text-sm hover:bg-white/10 transition-colors"
                  data-testid="mnav-login"
                >
                  <User className="h-4 w-4" /> {t("header.utility.client_login")}
                </Link>
                <Link
                  to="/login?role=admin"
                  onClick={() => setMobileOpen(false)}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-amber-400/40 bg-amber-500/10 text-amber-200 font-semibold px-4 py-2.5 text-sm hover:bg-amber-500/20 transition-colors"
                  data-testid="mnav-admin-login"
                >
                  <ShieldCheck className="h-4 w-4" /> {t("header.utility.admin_login")}
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <Link
                  to={dashboardPath}
                  onClick={() => setMobileOpen(false)}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/[0.04] text-white font-semibold px-4 py-2.5 text-sm"
                  data-testid="mnav-dashboard"
                >
                  {user.role === "admin" ? <ShieldCheck className="h-4 w-4" /> : <LayoutDashboard className="h-4 w-4" />}
                  Dashboard
                </Link>
                <button
                  onClick={async () => { await logout(); setMobileOpen(false); navigate("/"); }}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/[0.04] text-white/80 font-semibold px-4 py-2.5 text-sm"
                  data-testid="mnav-logout"
                >
                  <LogOut className="h-4 w-4" /> {t("common.sign_out")}
                </button>
              </div>
            )}

            {/* Language switcher (mobile drawer) */}
            <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between gap-3">
              <span className="text-xs font-semibold text-white/60 uppercase tracking-wider">
                {t("header.lang.label")}
              </span>
              <LanguageSwitcher variant="pills" />
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
